import type { FC } from "react"

import clsx from "clsx"

import type { ConcertAssignmentUserStatusDto } from "~/api/dto/concert.dto"

type ConcertUserStatusFailureKey =
  | `userExist`
  | `userLicenseExist`
  | `userOnboardingCompleted`

type ConcertUserStatusFailure = {
  key: ConcertUserStatusFailureKey
  message: string
}

export const getConcertUserStatusFailures = (
  status: ConcertAssignmentUserStatusDto | undefined,
): ConcertUserStatusFailure[] => {
  if (!status) {
    return []
  }

  const failures: ConcertUserStatusFailure[] = []

  if (!status.userExist) {
    failures.push({
      key: `userExist`,
      message: `A user account has not been created for you in the Concert portal.`,
    })
  }

  if (!status.userOnboardingCompleted) {
    failures.push({
      key: `userOnboardingCompleted`,
      message: `You have not completed the Concert onboarding process.`,
    })
  }

  if (!status.userLicenseExist) {
    failures.push({
      key: `userLicenseExist`,
      message: `You do not have a valid license added in the Concert portal.`,
    })
  }

  return failures
}

type ConcertAccountStatusBannerProps = {
  failures: ConcertUserStatusFailure[]
  className?: string
  onRequestAccount?: () => void
}

export const ConcertAccountStatusBanner: FC<ConcertAccountStatusBannerProps> = ({
  failures,
  className,
  onRequestAccount,
}) => {
  if (failures.length === 0) {
    return null
  }

  // Only where the caller can open the request form; a button that does
  // nothing would leave the seller stuck.
  const showRequestAccountAction =
    onRequestAccount != null &&
    failures.some((failure) => failure.key === `userExist`)

  return (
    <div
      className={clsx(
        `rounded bg-warning/10 p-4 text-sm text-foreground`,
        className,
      )}
      role="alert"
    >
      <p className="mb-2 font-semibold">Concert Account Setup Required</p>
      <p className="mb-2">
        Your Concert account must be fully configured before you can submit an
        application. Please resolve the following:
      </p>
      <ul className="list-disc space-y-1 pl-5">
        {failures.map((failure) => (
          <li key={failure.key}>{failure.message}</li>
        ))}
      </ul>
      {showRequestAccountAction && (
        <button
          type="button"
          onClick={onRequestAccount}
          className="mt-3 cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:opacity-90"
        >
          Request a Concert User Account
        </button>
      )}
      <p className="mt-3 text-muted">
        Contact your Concert portal administrator or Powur support for
        assistance.
      </p>
      {failures.some((f) => f.key === `userOnboardingCompleted`) && (
        <p className="mt-2 text-muted">
          If you recently created your account, check your welcome email from
          Concert to complete onboarding.
        </p>
      )}
    </div>
  )
}

type ConcertAccountStatusErrorBannerProps = {
  className?: string
}

export const ConcertAccountStatusErrorBanner: FC<
  ConcertAccountStatusErrorBannerProps
> = ({ className }) => {
  return (
    <div
      className={clsx(
        `rounded bg-warning/10 p-4 text-sm text-foreground`,
        className,
      )}
      role="alert"
    >
      <p className="mb-1 font-semibold">Concert account status unavailable</p>
      <p>
        We couldn&apos;t verify your Concert account right now. You can still
        proceed, but if your account isn&apos;t fully configured the submission
        may be rejected.
      </p>
    </div>
  )
}

type ConcertAccountStatusLoadingBannerProps = {
  className?: string
}

export const ConcertAccountStatusLoadingBanner: FC<
  ConcertAccountStatusLoadingBannerProps
> = ({ className }) => {
  return (
    <div
      className={clsx(
        `flex items-center gap-3 rounded border border-border bg-surface p-3 text-sm text-muted`,
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-transparent" />
      <span>Checking Concert account status…</span>
    </div>
  )
}
